import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';

import {
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  Tooltip
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { Pagination, PaginationItem } from '@material-ui/lab';
import { TEACHERS_PERMISSION_DENIED_URL } from 'config/urls';
import { debounce } from 'lodash';
import { useStudentGoalProgressList, useUserSections } from 'sdk';
import { cutText } from 'utils';

import NavTitle from 'pages/Teachers/Home/components/NavTitle';
import StudentRow from 'pages/Teachers/StudentListV2/components/StudentRow';
import { useNavbar } from 'pages/Teachers/shared';
import AddGoalDialog from 'pages/Teachers/shared/AddGoalDialog';
import { trackMixpanelEvent } from 'utils/integrations/mixpanel';

import UserContext from 'components/UserContext';

import { ReactComponent as BackIcon } from './assets/back-icon.svg';
import { ReactComponent as SearchIcon } from './assets/search-icon.svg';
import { ReactComponent as SettingsIcon } from './assets/settings-icon.svg';
import styles from './styles.module.scss';

const STUDENT_DETAIL_VIEW_ENABLED =
  process.env.REACT_APP_STUDENT_DETAIL_VIEW_ENABLED === 'true';

const StudentListV2 = () => {
  const { user } = useContext(UserContext);
  const history = useHistory();
  const location = useLocation();

  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isGoalDialogOpen, setIsGoalDialogOpen] = useState(false);

  const searchParams = new URLSearchParams(location.search);
  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  const [anchorEl, setAnchorEl] = useState(null);
  const [sectionValue, setSectionValue] = useState('placeholder');

  const PAGE_LIMIT = 6;

  const filters = useMemo(
    () => ({
      search: searchQuery,
      page: currentPage,
      limit: PAGE_LIMIT
    }),
    [searchQuery, currentPage]
  );

  const { data, isLoading, isValidating, refetch } = useStudentGoalProgressList(
    {
      filters
    }
  );

  const { data: userSectionsData } = useUserSections({
    params: {}
  });

  const userSections = userSectionsData || [];

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setSearchQuery(value);

        const params = new URLSearchParams(history.location.search);
        params.set('page', '1');
        history.push({ search: params.toString() });
      }, 500),
    [history]
  );

  const handleSearch = useCallback(
    (e) => {
      debouncedSearch(e.target.value);
      if (sectionValue !== 'placeholder') {
        setSectionValue('placeholder');
      }
    },
    [debouncedSearch, sectionValue]
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    if (refetch) {
      refetch();
    }
  }, [refetch, searchQuery, currentPage]);

  const navConfig = useMemo(
    () => ({
      title: (
        <NavTitle
          title={'Student Goal Progress'}
          subtitle={
            'Review each student’s recent uploads, goal progress, and next steps.'
          }
          Icon={BackIcon}
        />
      ),
      isTransparent: true
    }),
    []
  );

  useNavbar(navConfig);

  if (user.is_in_free_trial) {
    return <Redirect to={TEACHERS_PERMISSION_DENIED_URL} />;
  }

  const students = data?.results || [];
  const totalRecords = data?.count || 0;
  const totalPages = Math.ceil(totalRecords / PAGE_LIMIT) || 1;

  const handlePageChange = (event, value) => {
    const params = new URLSearchParams(location.search);
    params.set('page', value.toString());
    history.push({ search: params.toString() });
  };

  const handleSectionSelect = (selectedId) => {
    setSectionValue(selectedId);

    if (selectedId !== 'placeholder') {
      const selectedSection = userSections.find((s) => s.id === selectedId);
      if (selectedSection) {
        setInputValue(selectedSection.name);
        debouncedSearch(selectedSection.name);
      }
    } else {
      setInputValue('');
      debouncedSearch('');
    }

    setAnchorEl(null);
  };

  const handleAddGoalClick = () => {
    trackMixpanelEvent(user.id, 'User starts to add a goal', {
      feature: 'students',
      view: 'list',
      button: 'add',
      normalizedURL:
        process.env.REACT_APP_BASE_FRONTEND_URL + TEACHERS_PERMISSION_DENIED_URL
    });
    setIsGoalDialogOpen(true);
  };

  return (
    <>
      {isGoalDialogOpen && (
        <AddGoalDialog
          isOpen={isGoalDialogOpen}
          onAdd={() => {
            setIsGoalDialogOpen(false);
          }}
          onClose={() => {
            setIsGoalDialogOpen(false);
          }}
          isFromStudentList={true}
        />
      )}

      <div className={styles.container}>
        <div className={styles.actionsContainer}>
          <div className={styles.searchContainer}>
            <SettingsIcon
              onClick={(e) => setAnchorEl(e.currentTarget)}
              className={styles.settingIcon}
            />
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search Students, or Classes here..."
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                handleSearch(e);
              }}
            />
            <SearchIcon />
          </div>

          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            className={styles.addGoalButton}
            onClick={handleAddGoalClick}
          >
            ADD GOAL
          </Button>
        </div>

        <Menu
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          disablePortal={true}
        >
          <MenuItem onClick={() => handleSectionSelect('placeholder')}>
            Assignment Section
          </MenuItem>
          {userSections.map(({ id, name }) => (
            <MenuItem
              key={id}
              onClick={() => handleSectionSelect(id)}
              className={styles.menuItem}
            >
              <Tooltip title={name}>
                <span className={styles.menuItemText}>
                  {cutText({ text: name, char: 35 })}
                </span>
              </Tooltip>
            </MenuItem>
          ))}
        </Menu>

        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Student Name</th>
                <th>Section</th>
                <th>Recent Upload</th>
                <th>{STUDENT_DETAIL_VIEW_ENABLED ? 'Goal Progress' : ''}</th>
              </tr>
            </thead>
            <tbody key={currentPage}>
              {isLoading || isValidating ? (
                <tr>
                  <td colSpan="4" className={styles.centerText}>
                    <div className={styles.loaderContainer}>
                      <CircularProgress className={styles.circularProgress} />
                    </div>
                  </td>
                </tr>
              ) : students.length === 0 ? (
                <tr>
                  <td colSpan="4" className={styles.emptyState}>
                    No students found matching that criteria.
                  </td>
                </tr>
              ) : (
                students.map((student, index) => (
                  <StudentRow
                    key={`${student.id || index}-${currentPage}`}
                    student={student}
                    currentPage={currentPage}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            className={styles.pagination}
            renderItem={(item) => <PaginationItem {...item} disableRipple />}
          />
        )}
      </div>
    </>
  );
};

export default StudentListV2;
